import { Platform } from 'react-native';

import type { Package } from '../store/types';
import { cleverTapService } from './clevertap/clevertap.service';
import { analyticsEvents } from './firebase/analytics-events.service';

export type DonationType = 'CHIPS' | 'VIP';
export type AnalyticsPlatform = 'web' | 'ios' | 'android';

/**
 * The app-side donation funnel and exactly the properties the spec gives each event. The rest of
 * the spec's events (delivery, chat unlock) are sent by the backend. Ids, amounts and codes only:
 * never payment details or message text.
 */
type DonationEventProperties = {
	donation_profile_action_clicked: {
		sender_id: string;
		recipient_id: string;
		donation_type: DonationType;
		platform: AnalyticsPlatform;
	};
	donation_store_opened: {
		sender_id: string;
		recipient_id: string;
		donation_type: DonationType;
		platform: AnalyticsPlatform;
	};
	donation_product_selected: {
		donation_type: DonationType;
		product_id: string;
		product_value: number;
	};
	donation_checkout_started: {
		donation_type: DonationType;
		product_id: string;
		price: number;
		currency: string;
		platform: AnalyticsPlatform;
	};
	donation_payment_failed: {
		donation_type: DonationType;
		product_id: string;
		error_code: string;
	};
};

export type DonationEventName = keyof DonationEventProperties;

/** One donation funnel event to both the store's sinks, CleverTap and Firebase. */
export function trackDonationEvent<E extends DonationEventName>(
	eventName: E,
	properties: DonationEventProperties[E],
) {
	cleverTapService.trackEvent(eventName, properties);
	analyticsEvents.trackDonation(eventName, properties);
}

export const getAnalyticsPlatform = (): AnalyticsPlatform =>
	Platform.OS === 'ios' ? 'ios' : Platform.OS === 'android' ? 'android' : 'web';

export const donationTypeOfTab = (tab: string | undefined): DonationType =>
	tab === 'vip' ? 'VIP' : 'CHIPS';

export const donationTypeOfPackage = (pkg: Package): DonationType =>
	pkg.type === 'VIP_MEMBERSHIP' ? 'VIP' : 'CHIPS';

/** What the package gives: the chip amount, or the VIP days. */
export const donationProductValue = (pkg: Package): number =>
	Number(
		(pkg.type === 'VIP_MEMBERSHIP' ? pkg.vipDurationDays : (pkg.chipAmount ?? pkg.chips)) ?? 0,
	);

/** The package's currency code; the API sends it as a string, an object or an array of either. */
export const donationCurrency = (pkg: Package): string => {
	const raw: unknown = Array.isArray(pkg.currency) ? pkg.currency[0] : pkg.currency;
	if (typeof raw === 'string' && raw) return raw;
	const named = raw as { code?: unknown; name?: unknown } | null | undefined;
	if (typeof named?.code === 'string' && named.code) return named.code;
	if (typeof named?.name === 'string' && named.name) return named.name;
	return 'EUR';
};

/**
 * A short stable code for a failed payment. The payment errors reach the store already mapped to
 * a translation key (`T_PAYMENT_FAILED`, `T_NETWORK_ERROR_RETRY`, ...) or carry the server's own
 * error key; anything else is free text that may quote the server or the store, so it is
 * reported as `unknown` and never sent as is.
 */
export const donationErrorCode = (error: string | null | undefined): string => {
	const value = error?.trim();
	if (!value) return 'unknown';
	if (/^[A-Z][A-Z0-9_]{1,63}$/.test(value) || /^\d{3}$/.test(value)) return value;
	return 'unknown';
};
